import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Play, Eye, Wallet, Download, Pencil, FileText, TrendingUp, TrendingDown, Users, IndianRupee, BarChart3, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, isHR, getUser } from '@/lib/auth';
import api from '@/lib/api';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function canAccessFullPayroll() {
  const user = getUser();
  return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'hr';
}

// Estimated monthly in-hand at full attendance: Gross − employee PF − employee ESIC
// (mirrors the payroll engine's full-month math; flat ₹3,000 PF ceiling for Basic+DA > ₹25,000)
function inHandOf(s) {
  const bd = s.basic_da_rate || 0;
  const pf = s.pf_applicable ? (bd > 25000 ? 3000 : Math.round(bd * 0.12)) : 0;
  const esic = (s.esic_applicable && bd <= 21000) ? Math.round(bd * 0.0075) : 0;
  return (s.gross_rate || 0) - pf - esic;
}

export default function PayrollPage() {
  const fullAccess = canAccessFullPayroll();

  if (fullAccess) {
    return <AdminPayrollView />;
  }
  return <EmployeePayrollView />;
}


function EmployeePayrollView() {
  const [mySlips, setMySlips] = useState([]);
  const [mySalary, setMySalary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slipOpen, setSlipOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);

  const fmt = (v) => `\u20B9${(v || 0).toLocaleString('en-IN')}`;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [slipsRes, salaryRes] = await Promise.all([
          api.get('/api/payroll/my-payslips'),
          api.get('/api/payroll/my-salary')
        ]);
        setMySlips(slipsRes.data || []);
        setMySalary(salaryRes.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const handleDownloadPdf = async (slipId, empCode, m, y) => {
    try {
      const response = await api.get(`/api/payroll/payslip/${slipId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip_${empCode}_${String(m).padStart(2, '0')}_${y}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch (e) { toast.error('Failed to download payslip'); }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div>
        <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// MY PAYROLL</Badge>
        <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">My Salary</h1>
        <p className="text-sm text-slate-500">View and download your salary slips</p>
      </div>

      <Tabs defaultValue="payslips">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="payslips" data-testid="my-payslips-tab">My Payslips</TabsTrigger>
          <TabsTrigger value="salary" data-testid="my-salary-tab">My Salary Structure</TabsTrigger>
        </TabsList>

        <TabsContent value="payslips" className="mt-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Salary Slips</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6"><Skeleton className="h-40" /></div>
              ) : mySlips.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">No payslips available yet</p>
                  <p className="text-xs text-slate-400 mt-1">Your salary slips will appear here once payroll is processed</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Period</TableHead>
                        <TableHead>Gross Earned</TableHead>
                        <TableHead>Deductions</TableHead>
                        <TableHead>Net Payable</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mySlips.map(ps => (
                        <TableRow key={ps.id} data-testid={`my-payslip-row-${ps.id}`}>
                          <TableCell className="font-medium text-sm">{MONTHS[ps.month - 1]} {ps.year}</TableCell>
                          <TableCell className="text-sm font-tabular">{fmt(ps.gross_earned)}</TableCell>
                          <TableCell className="text-sm font-tabular text-red-600">{fmt(ps.deductions?.total)}</TableCell>
                          <TableCell className="text-sm font-tabular font-semibold text-emerald-700">{fmt(ps.net_payable)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="sm" onClick={() => { setSelectedSlip(ps); setSlipOpen(true); }} data-testid={`my-view-slip-${ps.id}`}>
                                <Eye className="w-3 h-3 mr-1" />View
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="salary" className="mt-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">My Salary Structure</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-40" />
              ) : !mySalary ? (
                <div className="p-8 text-center">
                  <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">Salary structure not available</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="my-salary-details">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <SalaryCard label="Basic + DA" value={fmt(mySalary.basic_da_rate)} />
                    <SalaryCard label="HRA" value={fmt(mySalary.hra)} />
                    <SalaryCard label="Conveyance" value={fmt(mySalary.conveyance)} />
                    {mySalary.other_allowance > 0 && <SalaryCard label="Other Allowance" value={fmt(mySalary.other_allowance)} />}
                    {mySalary.medical > 0 && <SalaryCard label="Medical" value={fmt(mySalary.medical)} />}
                  </div>
                  <div className="border-t pt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <SalaryCard label="Gross Salary" value={fmt(mySalary.gross_rate)} highlight />
                    <SalaryCard label="PF Applicable" value={mySalary.pf_applicable ? 'Yes' : 'No'} />
                    <SalaryCard label="ESIC Applicable" value={mySalary.esic_applicable ? 'Yes' : 'No'} />
                  </div>
                  {mySalary.kra_amount > 0 && (
                    <div className="border-t pt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <SalaryCard label="KRA Incentive" value={fmt(mySalary.kra_amount)} />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PayslipDetailDialog
        open={slipOpen}
        onOpenChange={setSlipOpen}
        slip={selectedSlip}
        onDownload={handleDownloadPdf}
      />
    </div>
  );
}


function SalaryCard({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-lg ${highlight ? 'bg-[#0D2850]/5 border border-[#0D2850]/10' : 'bg-slate-50'}`}>
      <p className="text-[10px] font-medium text-slate-500 uppercase">{label}</p>
      <p className={`text-sm font-semibold mt-1 ${highlight ? 'text-[#0D2850]' : 'text-slate-800'}`}>{value}</p>
    </div>
  );
}


function AdminPayrollView() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [runs, setRuns] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewRun, setViewRun] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [slipOpen, setSlipOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editStruct, setEditStruct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const admin = isAdmin();

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/api/payroll/runs'),
        api.get('/api/payroll/salary-structures')
      ]);
      setRuns(r.data);
      setStructures(s.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreateRun = async () => {
    try {
      const { data } = await api.post('/api/payroll/runs', { month, year });
      const skipped = data.skipped_count || 0;
      if (skipped > 0) {
        const names = (data.skipped_employees || []).map(s => `• ${s.name} (${s.employee_code}) — ${s.reason}`).join('\n');
        toast.warning(`Payroll created: ${data.period} — but ${skipped} employee(s) skipped (no salary structure):\n${names}`, { duration: 10000 });
      } else {
        toast.success(`Payroll created: ${data.period} for ${data.employees_count} employees`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handlePreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const { data } = await api.get(`/api/payroll/preview?month=${month}&year=${year}`);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
      setPreviewOpen(false);
    }
    setPreviewLoading(false);
  };

  const handleViewRun = async (run) => {
    try {
      const { data } = await api.get(`/api/payroll/runs/${run.id}/payslips`);
      setPayslips(data);
      setViewRun(run);
    } catch (e) { toast.error('Failed to load payslips'); }
  };

  const handleDeleteRun = async (run) => {
    const confirm1 = window.confirm(
      `Delete the payroll run for ${run.period}?\n\n` +
      `This will permanently remove the run + ${run.employees_count || 'all'} payslip(s) ` +
      `from the database (audit logged).\n\n` +
      `Use this only when you intend to regenerate the run with updated attendance / leave data.\n\n` +
      `Type-confirm in the next dialog.`
    );
    if (!confirm1) return;
    const reason = window.prompt(
      `Final confirmation — please enter a reason for deleting ${run.period}:`,
      'Regenerate with updated attendance / new policy'
    );
    if (!reason || !reason.trim()) {
      toast.info('Deletion cancelled (no reason provided)');
      return;
    }
    try {
      const { data } = await api.delete(`/api/payroll/runs/${run.id}`, { params: { reason } });
      toast.success(data.message);
      // Refresh list
      const r = await api.get('/api/payroll/runs');
      setRuns(r.data);
      if (viewRun?.id === run.id) setViewRun(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete payroll run');
    }
  };

  const handleDownloadPdf = async (slipId, empCode, m, y) => {
    try {
      const response = await api.get(`/api/payroll/payslip/${slipId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip_${empCode}_${String(m).padStart(2, '0')}_${y}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch (e) { toast.error('Failed to download payslip'); }
  };

  const openEdit = (s) => {
    setEditStruct(s);
    setEditForm({
      basic_da_rate: s.basic_da_rate || 0,
      hra: s.hra || 0,
      conveyance: s.conveyance || 0,
      other_allowance: s.other_allowance || 0,
      medical: s.medical || 0,
      kra_amount: s.kra_amount || 0,
      pf_applicable: s.pf_applicable ?? false,
      esic_applicable: s.esic_applicable ?? false,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editStruct) return;
    setSaving(true);
    try {
      await api.put(`/api/payroll/salary-structures/${editStruct.id}`, editForm);
      toast.success(`Updated salary structure for ${editStruct.employee_name}`);
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    setSaving(false);
  };

  const fmt = (v) => `\u20B9${(v || 0).toLocaleString('en-IN')}`;

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// PAYROLL</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Payroll</h1>
          <p className="text-sm text-slate-500">Manage payroll, salary structures, and view salary slips</p>
        </div>
      </div>

      <Tabs defaultValue="runs">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="runs">Payroll Runs</TabsTrigger>
          <TabsTrigger value="structures">Salary Structures</TabsTrigger>
          <TabsTrigger value="comparison">Comparison</TabsTrigger>
          <TabsTrigger value="reimbursement" data-testid="reimbursement-tab">Salary Reimbursement</TabsTrigger>
        </TabsList>

        <TabsContent value="runs" className="mt-4 space-y-4">
          <Card className="border-slate-200">
            <CardContent className="py-4 px-5">
              <h3 className="text-sm font-semibold mb-3">Create Payroll Run</h3>
              <div className="flex gap-3 items-end flex-wrap">
                <div>
                  <label className="text-xs text-slate-500">Month</label>
                  <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
                    <SelectTrigger className="w-[150px] h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Year</label>
                  <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                    <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button className="bg-[#0D2850] hover:bg-[#0B2246] h-9" onClick={handleCreateRun} data-testid="payroll-run-now-button"><Play className="w-4 h-4 mr-1" />Create Payroll</Button>
                <Button variant="outline" className="h-9" onClick={handlePreview} disabled={previewLoading} data-testid="payroll-preview-button" title="Live recompute for the selected month — validates salary against actual attendance without saving anything">
                  <Eye className="w-4 h-4 mr-1" />{previewLoading ? 'Computing…' : 'Preview Salary'}
                </Button>
                <SalaryPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} preview={preview} loading={previewLoading} fmt={fmt} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Payroll History</CardTitle></CardHeader>
            <CardContent className="p-0">
              {loading ? <div className="p-6"><Skeleton className="h-40" /></div> : runs.length === 0 ? (
                <div className="p-12 text-center"><Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-400">No payroll runs yet</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50"><TableHead>Period</TableHead><TableHead>Employees</TableHead><TableHead>Gross</TableHead><TableHead>Net</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {runs.map(r => (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium text-sm">{r.period}</TableCell>
                          <TableCell className="text-sm">
                            {r.employees_count}
                            {(r.skipped_count || 0) > 0 && (
                              <Badge className="ml-1 bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]" title={(r.skipped_employees || []).map(s => `${s.name} (${s.employee_code}) — ${s.reason}`).join('\n')} data-testid={`skipped-badge-${r.id}`}>
                                {r.skipped_count} skipped
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm font-tabular">{fmt(r.total_gross)}</TableCell>
                          <TableCell className="text-sm font-tabular">{fmt(r.total_net)}</TableCell>
                          <TableCell><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{r.status}</Badge></TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="outline" size="sm" onClick={() => handleViewRun(r)} data-testid={`view-payroll-${r.id}`}><Eye className="w-3 h-3 mr-1" />View</Button>
                              {(isAdmin() || isHR()) && (
                                <Button
                                  variant="outline" size="sm"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleDeleteRun(r)}
                                  data-testid={`delete-payroll-${r.id}`}
                                  title="Delete this run (e.g. to regenerate with updated attendance)"
                                ><Trash2 className="w-3 h-3" /></Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {viewRun && (
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <CardTitle className="text-sm font-semibold">Payslips - {viewRun.period}</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="gap-1" data-testid="export-payroll-csv"
                      onClick={async () => { try { const r = await api.get(`/api/payroll/export?month=${viewRun.month}&year=${viewRun.year}&format=csv`, {responseType: 'blob'}); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = `payroll_${viewRun.period.replace('/', '_')}.csv`; a.click(); window.URL.revokeObjectURL(u); toast.success('CSV exported'); } catch(e) { toast.error('Export failed'); } }}>
                      <Download className="w-3 h-3" />CSV
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1" data-testid="export-payroll-pdf"
                      onClick={async () => { try { const r = await api.get(`/api/payroll/export?month=${viewRun.month}&year=${viewRun.year}&format=pdf`, {responseType: 'blob'}); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = `payroll_${viewRun.period.replace('/', '_')}.pdf`; a.click(); window.URL.revokeObjectURL(u); toast.success('PDF exported'); } catch(e) { toast.error('Export failed'); } }}>
                      <Download className="w-3 h-3" />PDF
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setViewRun(null)}>Close</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50"><TableHead>Employee</TableHead><TableHead>Dept</TableHead><TableHead>Gross</TableHead><TableHead>Deductions</TableHead><TableHead>Net Payable</TableHead><TableHead>CTC</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {payslips.map(ps => (
                      <TableRow key={ps.id}>
                        <TableCell><p className="text-sm font-medium">{ps.employee_name}</p><p className="text-xs text-slate-400">{ps.employee_code}</p></TableCell>
                        <TableCell className="text-sm">{ps.department}</TableCell>
                        <TableCell className="text-sm font-tabular">{fmt(ps.gross_earned)}</TableCell>
                        <TableCell className="text-sm font-tabular text-red-600">{fmt(ps.deductions?.total)}</TableCell>
                        <TableCell className="text-sm font-tabular font-semibold">{fmt(ps.net_payable)}</TableCell>
                        <TableCell className="text-sm font-tabular">{fmt(ps.ctc)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedSlip(ps); setSlipOpen(true); }} data-testid={`view-slip-${ps.id}`}><Eye className="w-3 h-3 mr-1" />View</Button>
                            <Button variant="ghost" size="sm" className="text-[#0D2850]" onClick={() => handleDownloadPdf(ps.id, ps.employee_code, ps.month, ps.year)} data-testid={`download-slip-${ps.id}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="structures" className="mt-4">
          <Card className="border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-medium text-slate-700">Salary Structures <span className="text-slate-400 font-normal">({structures.length} employees)</span></p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1" data-testid="export-structures-csv"
                  onClick={async () => { try { const r = await api.get('/api/payroll/salary-structures/export?format=csv', {responseType: 'blob'}); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'salary_structures.csv'; a.click(); window.URL.revokeObjectURL(u); toast.success('CSV exported'); } catch(e) { toast.error('Export failed'); } }}>
                  <Download className="w-3 h-3" />CSV
                </Button>
                <Button variant="outline" size="sm" className="gap-1" data-testid="export-structures-pdf"
                  onClick={async () => { try { const r = await api.get('/api/payroll/salary-structures/export?format=pdf', {responseType: 'blob'}); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'salary_structures.pdf'; a.click(); window.URL.revokeObjectURL(u); toast.success('PDF exported'); } catch(e) { toast.error('Export failed'); } }}>
                  <Download className="w-3 h-3" />PDF
                </Button>
              </div>
            </div>
            <CardContent className="p-0 overflow-x-auto">
              {loading ? <div className="p-6"><Skeleton className="h-40" /></div> : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Employee</TableHead><TableHead>Basic+DA</TableHead><TableHead>HRA</TableHead><TableHead>Conv</TableHead><TableHead>Other</TableHead><TableHead>Medical</TableHead><TableHead>Gross</TableHead><TableHead>KRA</TableHead><TableHead>CTC</TableHead><TableHead>In Hand</TableHead><TableHead>PF</TableHead><TableHead>ESIC</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {structures.map(s => (
                      <TableRow key={s.id}>
                        <TableCell><p className="text-sm font-medium">{s.employee_name}</p><p className="text-xs text-slate-400">{s.employee_code}</p></TableCell>
                        <TableCell className="text-sm font-tabular">{fmt(s.basic_da_rate)}</TableCell>
                        <TableCell className="text-sm font-tabular">{fmt(s.hra)}</TableCell>
                        <TableCell className="text-sm font-tabular">{fmt(s.conveyance)}</TableCell>
                        <TableCell className="text-sm font-tabular">{fmt(s.other_allowance)}</TableCell>
                        <TableCell className="text-sm font-tabular">{fmt(s.medical)}</TableCell>
                        <TableCell className="text-sm font-tabular font-semibold">{fmt(s.gross_rate)}</TableCell>
                        <TableCell className="text-sm font-tabular">{fmt(s.kra_amount)}</TableCell>
                        <TableCell className="text-sm font-tabular font-semibold">{fmt(s.current_ctc)}</TableCell>
                        <TableCell className="text-sm font-tabular font-semibold text-emerald-700" data-testid={`inhand-struct-${s.id}`}>{fmt(inHandOf(s))}</TableCell>
                        <TableCell><Badge className={s.pf_applicable ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-100'}>{s.pf_applicable ? 'Yes' : 'No'}</Badge></TableCell>
                        <TableCell><Badge className={s.esic_applicable ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-100'}>{s.esic_applicable ? 'Yes' : 'No'}</Badge></TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-salary-${s.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <PayrollComparison />

        <TabsContent value="reimbursement" className="mt-4">
          <SalaryReimbursementTab fmt={fmt} />
        </TabsContent>
      </Tabs>

      <PayslipDetailDialog
        open={slipOpen}
        onOpenChange={setSlipOpen}
        slip={selectedSlip}
        onDownload={handleDownloadPdf}
      />

      {/* Edit Salary Structure Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Edit Salary - {editStruct?.employee_name}</DialogTitle>
            <p className="text-xs text-slate-500">{editStruct?.employee_code} | Use this for increments or minimum wage changes</p>
          </DialogHeader>
          {editStruct && (
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Earnings Components</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Basic + DA</Label>
                  <Input type="number" value={editForm.basic_da_rate} onChange={e => setEditForm(p => ({ ...p, basic_da_rate: parseFloat(e.target.value) || 0 }))} data-testid="edit-basic-da" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">HRA</Label>
                  <Input type="number" value={editForm.hra} onChange={e => setEditForm(p => ({ ...p, hra: parseFloat(e.target.value) || 0 }))} data-testid="edit-hra" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Conveyance</Label>
                  <Input type="number" value={editForm.conveyance} onChange={e => setEditForm(p => ({ ...p, conveyance: parseFloat(e.target.value) || 0 }))} data-testid="edit-conveyance" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Other Allowance</Label>
                  <Input type="number" value={editForm.other_allowance} onChange={e => setEditForm(p => ({ ...p, other_allowance: parseFloat(e.target.value) || 0 }))} data-testid="edit-other-allowance" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Medical</Label>
                  <Input type="number" value={editForm.medical} onChange={e => setEditForm(p => ({ ...p, medical: parseFloat(e.target.value) || 0 }))} data-testid="edit-medical" />
                </div>
              </div>

              <div className="bg-slate-50 p-2 rounded text-xs text-slate-600">
                <span className="font-medium">New Gross = </span>
                {fmt((editForm.basic_da_rate || 0) + (editForm.hra || 0) + (editForm.conveyance || 0) + (editForm.other_allowance || 0) + (editForm.medical || 0))}
                {editStruct.gross_rate > 0 && (
                  <span className="ml-2 text-slate-400">(was {fmt(editStruct.gross_rate)})</span>
                )}
              </div>

              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider pt-1">Incentives</p>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">KRA Incentive</Label>
                  <Input type="number" value={editForm.kra_amount} onChange={e => setEditForm(p => ({ ...p, kra_amount: parseFloat(e.target.value) || 0 }))} data-testid="edit-kra-input" />
                </div>
              </div>

              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider pt-1">Statutory</p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={editForm.pf_applicable} onChange={e => setEditForm(p => ({ ...p, pf_applicable: e.target.checked }))} className="rounded" data-testid="edit-pf-check" />
                  PF Applicable
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={editForm.esic_applicable} onChange={e => setEditForm(p => ({ ...p, esic_applicable: e.target.checked }))} className="rounded" data-testid="edit-esic-check" />
                  ESIC Applicable
                </label>
              </div>

              <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleSaveEdit} disabled={saving} data-testid="save-salary-edit">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PayslipDetailDialog({ open, onOpenChange, slip, onDownload }) {
  const fmt = (v) => `\u20B9${(v || 0).toLocaleString('en-IN')}`;
  const canDownload = isAdmin() || isHR();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Salary Slip - {slip?.employee_name}</DialogTitle>
            {slip && canDownload && (
              <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246] gap-1" onClick={() => onDownload(slip.id, slip.employee_code, slip.month, slip.year)} data-testid="download-slip-dialog">
                <Download className="w-3.5 h-3.5" />Download PDF
              </Button>
            )}
          </div>
        </DialogHeader>
        {slip && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-b pb-3">
              <div><span className="text-slate-500">Period:</span> <span className="font-medium">{MONTHS[slip.month - 1]} {slip.year}</span></div>
              <div><span className="text-slate-500">Employee:</span> <span className="font-medium">{slip.employee_code}</span></div>
              <div><span className="text-slate-500">Monthly Days:</span> <span className="font-medium">{slip.monthly_days}</span></div>
              {(slip.leave_days > 0) && <div><span className="text-slate-500">Leave Days:</span> <span className="font-medium text-blue-600">{slip.leave_days}</span></div>}
              {(slip.lop_days > 0) && <div><span className="text-slate-500">Absent / LOP:</span> <span className="font-medium text-red-600">{slip.lop_days}</span></div>}
              <div><span className="text-slate-500">Payable Days:</span> <span className="font-medium">{slip.payable_days ?? slip.monthly_days}</span></div>
            </div>
            {/* Attendance & Leave Breakdown (new policy fields) */}
            {(slip.leave_breakdown || slip.day_remarks) && (
              <div className="border rounded-md p-3 bg-slate-50 space-y-2 text-xs" data-testid="payslip-breakdown">
                <h4 className="font-semibold text-xs uppercase text-slate-500 tracking-wide">Attendance & Leave Breakdown</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {slip.present_days != null && <div><span className="text-slate-500">Present:</span> <span className="font-semibold text-emerald-600">{slip.present_days} days</span></div>}
                  {slip.paid_off_days != null && <div><span className="text-slate-500">Sundays / Holidays:</span> <span className="font-medium">{slip.paid_off_days} days</span></div>}
                  {slip.leave_breakdown && Object.entries(slip.leave_breakdown).filter(([, v]) => v > 0).map(([lt, v]) => (
                    <div key={lt}><span className="text-slate-500">Adjusted via {lt}:</span> <span className="font-medium text-blue-600">{v} day(s)</span></div>
                  ))}
                  {slip.half_day_count > 0 && <div><span className="text-slate-500">Half-Day (3+ Late):</span> <span className="font-medium text-amber-600">{slip.half_day_count} (-{(slip.half_day_count * 0.5)} day)</span></div>}
                  {slip.absent_lop_days > 0 && <div><span className="text-slate-500">Absent (LOP):</span> <span className="font-semibold text-red-600">{slip.absent_lop_days} days</span></div>}
                </div>
                <p className="text-[10px] text-slate-400 italic">Day-by-day breakdown available in the downloaded PDF.</p>
              </div>
            )}
            <h4 className="font-semibold text-xs uppercase text-slate-500">Earnings</h4>
            {Object.entries(slip.earnings).filter(([k,v]) => v > 0 && k !== 'da').map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-slate-600 capitalize">{k.replace(/_/g, ' ')}</span><span className="font-tabular">{fmt(v)}</span></div>
            ))}
            <div className="flex justify-between font-semibold border-t pt-2"><span>Gross Earned</span><span className="font-tabular">{fmt(slip.gross_earned)}</span></div>
            <h4 className="font-semibold text-xs uppercase text-slate-500 mt-4">Deductions (Employee)</h4>
            {Object.entries(slip.deductions).filter(([k, v]) => !['total','pf_employer','esic_employer'].includes(k) && v > 0).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-slate-600 capitalize">{k.replace(/_/g, ' ')}</span><span className="font-tabular text-red-600">{fmt(v)}</span></div>
            ))}
            <div className="flex justify-between font-semibold border-t pt-2 text-lg"><span>Net Payable</span><span className="font-tabular text-emerald-700">{fmt(slip.net_payable)}</span></div>
            {(slip.deductions?.pf_employer > 0 || slip.deductions?.esic_employer > 0 || slip.provisions?.kra_incentive > 0) && (
              <>
                <h4 className="font-semibold text-xs uppercase text-slate-500 mt-4">Employer Contributions & Provisions</h4>
                {slip.deductions?.pf_employer > 0 && <div className="flex justify-between"><span className="text-slate-600">PF (Employer 12%)</span><span className="font-tabular">{fmt(slip.deductions.pf_employer)}</span></div>}
                {slip.deductions?.esic_employer > 0 && <div className="flex justify-between"><span className="text-slate-600">ESIC (Employer 3.25%)</span><span className="font-tabular">{fmt(slip.deductions.esic_employer)}</span></div>}
                {slip.provisions?.kra_incentive > 0 && <div className="flex justify-between"><span className="text-slate-600">KRA Incentive (Quarterly/Annual)</span><span className="font-tabular">{fmt(slip.provisions.kra_incentive)}</span></div>}
              </>
            )}
            <div className="flex justify-between text-xs text-slate-400 border-t pt-2"><span>CTC (incl. all contributions)</span><span className="font-tabular">{fmt(slip.ctc)}</span></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


const DEPT_COLORS = ['#0D2850','#1a6b4a','#b45309','#7c3aed','#dc2626','#0284c7','#be185d','#4f46e5','#059669','#d97706','#6366f1','#ec4899'];
const CHART_TOOLTIP_STYLE = { contentStyle: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, padding: '8px 12px' } };

function PayrollComparison() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/payroll/comparison').then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const fmt = (v) => `\u20B9${(v || 0).toLocaleString('en-IN')}`;
  const fmtK = (v) => `\u20B9${(v / 1000).toFixed(0)}K`;
  const fmtL = (v) => v >= 100000 ? `\u20B9${(v / 100000).toFixed(1)}L` : fmtK(v);

  const trendData = useMemo(() => [...data].reverse().map(d => ({
    period: d.period?.replace(/\s\d{4}/, m => `'${m.trim().slice(2)}`),
    gross: d.total_gross,
    net: d.total_net,
    employees: d.employees_count,
  })), [data]);

  const latestDeptData = useMemo(() => {
    if (!data[0]?.by_department) return [];
    return Object.entries(data[0].by_department)
      .map(([dept, d]) => ({ name: dept, value: d.gross, count: d.count, net: d.net }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const pfEsicData = useMemo(() => [...data].reverse().map(d => {
    let pf = 0, esic = 0;
    if (d.by_department) Object.values(d.by_department).forEach(dept => { pf += dept.pf || 0; esic += dept.esic || 0; });
    return { period: d.period?.replace(/\s\d{4}/, m => `'${m.trim().slice(2)}`), pf, esic };
  }), [data]);

  const latestRun = data[0];
  const prevRun = data[1];
  const grossChange = prevRun ? ((latestRun?.total_gross - prevRun.total_gross) / prevRun.total_gross * 100) : 0;

  if (loading) return <TabsContent value="comparison"><Skeleton className="h-40 mt-4" /></TabsContent>;

  return (
    <TabsContent value="comparison" className="mt-4 space-y-4" data-testid="payroll-comparison">
      {data.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-12 text-center"><BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-400">No payroll runs to compare. Run payroll for at least one month.</p></CardContent></Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-slate-200">
              <CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase font-medium">Total Gross ({latestRun?.period?.split(' ')[0]?.slice(0,3)})</p>
                <p className="text-xl font-bold text-[#0B1B33] mt-1">{fmtL(latestRun?.total_gross || 0)}</p>
                {prevRun && <div className={`flex items-center gap-1 mt-1 text-xs ${grossChange > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {grossChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {grossChange > 0 ? '+' : ''}{grossChange.toFixed(1)}% vs prev
                </div>}
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase font-medium">Total Net Pay</p>
                <p className="text-xl font-bold text-emerald-700 mt-1">{fmtL(latestRun?.total_net || 0)}</p>
                <p className="text-[10px] text-slate-400 mt-1">{latestRun?.employees_count} employees</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase font-medium">Avg per Employee</p>
                <p className="text-xl font-bold text-[#0B1B33] mt-1">{fmtK(latestRun?.employees_count ? latestRun.total_gross / latestRun.employees_count : 0)}</p>
                <p className="text-[10px] text-slate-400 mt-1">gross / month</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase font-medium">PF + ESIC</p>
                <p className="text-xl font-bold text-violet-700 mt-1">{fmtK((pfEsicData[pfEsicData.length - 1]?.pf || 0) + (pfEsicData[pfEsicData.length - 1]?.esic || 0))}</p>
                <p className="text-[10px] text-slate-400 mt-1">statutory contributions</p>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trend Chart */}
          {trendData.length > 1 && (
            <Card className="border-slate-200">
              <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Monthly Cost Trend</CardTitle></CardHeader>
              <CardContent className="p-3 pr-5">
                <div className="h-[250px]" data-testid="trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendData} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} width={60} />
                      <Tooltip formatter={(v) => fmt(v)} {...CHART_TOOLTIP_STYLE} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="gross" name="Gross" fill="#0D2850" radius={[4,4,0,0]} />
                      <Bar dataKey="net" name="Net" fill="#059669" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Department Distribution */}
            {latestDeptData.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Department Salary Share ({latestRun?.period?.split(' ')[0]?.slice(0,3)})</CardTitle></CardHeader>
                <CardContent className="p-3">
                  <div className="h-[230px]" data-testid="dept-pie-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={latestDeptData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 1 }} style={{ fontSize: 10 }}>
                          {latestDeptData.map((_, i) => <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => fmt(v)} {...CHART_TOOLTIP_STYLE} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* PF & ESIC Trend */}
            {pfEsicData.some(d => d.pf > 0 || d.esic > 0) && (
              <Card className="border-slate-200">
                <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">PF & ESIC Contributions</CardTitle></CardHeader>
                <CardContent className="p-3 pr-5">
                  <div className="h-[230px]" data-testid="pf-esic-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pfEsicData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} width={55} />
                        <Tooltip formatter={v => fmt(v)} {...CHART_TOOLTIP_STYLE} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="pf" name="PF (Total)" fill="#7c3aed" stackId="a" radius={[0,0,0,0]} />
                        <Bar dataKey="esic" name="ESIC (Total)" fill="#be185d" stackId="a" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Department Table */}
          <Card className="border-slate-200">
            <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Department Breakdown — {latestRun?.period}</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Department</TableHead>
                    <TableHead className="text-center">Headcount</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead className="text-right">PF</TableHead>
                    <TableHead className="text-right">ESIC</TableHead>
                    <TableHead className="text-right">Share</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {latestDeptData.map((dept, i) => (
                    <TableRow key={dept.name} data-testid={`dept-row-${dept.name}`}>
                      <TableCell className="font-medium text-sm flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DEPT_COLORS[i % DEPT_COLORS.length] }} />
                        {dept.name}
                      </TableCell>
                      <TableCell className="text-center text-sm">{dept.count}</TableCell>
                      <TableCell className="text-right text-sm font-tabular">{fmt(dept.value)}</TableCell>
                      <TableCell className="text-right text-sm font-tabular">{fmt(dept.net)}</TableCell>
                      <TableCell className="text-right text-sm font-tabular">{fmt(latestRun?.by_department?.[dept.name]?.pf || 0)}</TableCell>
                      <TableCell className="text-right text-sm font-tabular">{fmt(latestRun?.by_department?.[dept.name]?.esic || 0)}</TableCell>
                      <TableCell className="text-right text-sm">
                        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">
                          {latestRun?.total_gross ? ((dept.value / latestRun.total_gross) * 100).toFixed(1) : 0}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Month-over-Month Table */}
          <Card className="border-slate-200">
            <CardHeader className="py-3 px-5 pb-1"><CardTitle className="text-sm font-semibold">Month-over-Month Comparison</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Period</TableHead>
                    <TableHead className="text-center">Employees</TableHead>
                    <TableHead className="text-right">Total Gross</TableHead>
                    <TableHead className="text-right">Total Net</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((row, i) => {
                    const prev = data[i + 1];
                    const change = prev ? ((row.total_gross - prev.total_gross) / prev.total_gross * 100) : 0;
                    return (
                      <TableRow key={i} data-testid={`comparison-row-${i}`}>
                        <TableCell className="font-medium text-sm">{row.period}</TableCell>
                        <TableCell className="text-center text-sm">{row.employees_count}</TableCell>
                        <TableCell className="text-right text-sm font-tabular">{fmt(row.total_gross)}</TableCell>
                        <TableCell className="text-right text-sm font-tabular">{fmt(row.total_net)}</TableCell>
                        <TableCell className="text-right text-sm">
                          {prev ? (
                            <span className={change > 0 ? 'text-red-600' : change < 0 ? 'text-emerald-600' : 'text-slate-500'}>
                              {change > 0 ? '+' : ''}{change.toFixed(1)}%
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </TabsContent>
  );
}


function SalaryReimbursementTab({ fmt }) {
  const [advances, setAdvances] = useState([]);
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [empFilter, setEmpFilter] = useState('all');
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantForm, setGrantForm] = useState({ employee_id: '', amount: '', date: new Date().toISOString().slice(0, 10), reason: '', notes: '' });
  const [granting, setGranting] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryTarget, setRecoveryTarget] = useState(null);
  const [recoveryForm, setRecoveryForm] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), notes: '', mode: 'cash' });
  const [savingRecovery, setSavingRecovery] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState([]);
  const [deductEdits, setDeductEdits] = useState({});
  const [deductSaving, setDeductSaving] = useState({});
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [inhand, setInhand] = useState(null);
  const [inhandLoading, setInhandLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, s, e, r] = await Promise.all([
        api.get('/api/payroll/advances'),
        api.get('/api/payroll/advances/summary'),
        api.get('/api/employees'),
        api.get('/api/payroll/runs'),
      ]);
      setAdvances(a.data || []);
      setSummary(s.data);
      const empList = Array.isArray(e.data) ? e.data : (e.data?.employees || []);
      setEmployees(empList.filter(x => x.status === 'active'));
      const runList = Array.isArray(r.data) ? r.data : (r.data?.runs || []);
      setRuns(runList);
    } catch (err) { toast.error('Failed to load advances'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = advances.filter(a =>
    (statusFilter === 'all' || a.status === statusFilter) &&
    (empFilter === 'all' || a.employee_id === empFilter)
  );

  const handleGrant = async () => {
    if (!grantForm.employee_id) { toast.error('Select an employee'); return; }
    if (!grantForm.amount || Number(grantForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setGranting(true);
    try {
      await api.post('/api/payroll/advances', { ...grantForm, amount: Number(grantForm.amount) });
      toast.success('Advance granted');
      setGrantOpen(false);
      setGrantForm({ employee_id: '', amount: '', date: new Date().toISOString().slice(0, 10), reason: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to grant advance'); }
    setGranting(false);
  };

  const openRecovery = (adv) => {
    setRecoveryTarget(adv);
    setRecoveryForm({ amount: String(adv.balance), date: new Date().toISOString().slice(0, 10), notes: '', mode: 'cash' });
    setRecoveryOpen(true);
  };

  const handleRecovery = async () => {
    if (!recoveryForm.amount || Number(recoveryForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSavingRecovery(true);
    try {
      const { data } = await api.post(`/api/payroll/advances/${recoveryTarget.id}/recoveries`, { ...recoveryForm, amount: Number(recoveryForm.amount) });
      toast.success(`Recovery recorded — balance now ${fmt(data.balance)}`);
      setRecoveryOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record recovery'); }
    setSavingRecovery(false);
  };

  const handleCancel = async (adv) => {
    const reason = window.prompt(`Cancel advance of ${fmt(adv.amount)} for ${adv.employee_name}?\nEnter reason:`, 'Mistaken entry');
    if (reason === null) return;
    try {
      const { data } = await api.delete(`/api/payroll/advances/${adv.id}`, { params: { reason } });
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  const openHistory = async (adv) => {
    setHistoryTarget(adv);
    setHistory([]);
    setHistoryOpen(true);
    try {
      const { data } = await api.get(`/api/payroll/advances/${adv.id}/recoveries`);
      setHistory(data);
    } catch (e) { toast.error('Failed to load history'); }
  };

  const saveDeduction = async (emp) => {
    const raw = deductEdits[emp.employee_id];
    if (raw === undefined || raw === '') return;
    const amount = Number(raw);
    if (isNaN(amount) || amount < 0) { toast.error('Enter a valid deduction amount (0 to clear)'); return; }
    setDeductSaving(prev => ({ ...prev, [emp.employee_id]: true }));
    try {
      const payload = { employee_id: emp.employee_id, amount };
      if (selectedRun) { payload.month = selectedRun.month; payload.year = selectedRun.year; }
      const { data } = await api.post('/api/payroll/advances/deduct', payload);
      toast.success(amount > 0 ? `Deduction ${fmt(data.deducted)} saved — balance now ${fmt(data.new_outstanding)}` : 'Deduction cleared');
      if (selectedRun) handleViewInhand(selectedRun, true);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save deduction'); }
    setDeductSaving(prev => { const c = { ...prev }; delete c[emp.employee_id]; return c; });
  };

  const handleViewInhand = async (run, silent = false) => {
    if (!silent) { setInhand(null); setInhandLoading(true); }
    setSelectedRun(run);
    try {
      const { data } = await api.get(`/api/payroll/advances/inhand/${run.id}`);
      setInhand(data);
      const prefill = {};
      (data.rows || []).forEach(r => { prefill[r.employee_id] = r.month_deduction || 0; });
      setDeductEdits(prefill);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load sheet'); }
    setInhandLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="salary-reimbursement-tab">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SalaryCard label="Total Advanced" value={fmt(summary.total_advanced)} />
          <SalaryCard label="Total Recovered" value={fmt(summary.total_recovered)} />
          <SalaryCard label="Outstanding" value={fmt(summary.total_outstanding)} highlight />
          <SalaryCard label="Unrecovered (Exited)" value={summary.unrecovered_count} highlight={summary.unrecovered_count > 0} />
        </div>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-sm font-semibold">Actual In Hand Salary</CardTitle>
              <p className="text-[10px] text-slate-400 mt-1">Month-wise sheets, same history as Payroll Runs. Open a month to see Net Payable minus advance deductions and record deductions.</p>
            </div>
            <Button size="sm" className="bg-[#0D2850] hover:bg-[#0B2246] h-8 gap-1" onClick={() => setGrantOpen(true)} data-testid="grant-advance-btn">
              <Wallet className="w-3.5 h-3.5" /> Grant Advance
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? <div className="p-6"><Skeleton className="h-40" /></div> : runs.length === 0 ? (
            <div className="p-12 text-center"><Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-400">No payroll runs yet</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Period</TableHead><TableHead>Employees</TableHead>
                <TableHead className="text-right">Total Gross</TableHead><TableHead className="text-right">Total Net</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {runs.map(r => (
                  <TableRow key={r.id} data-testid={`inhand-run-${r.id}`}>
                    <TableCell className="font-medium text-sm">{r.period}</TableCell>
                    <TableCell className="text-sm">{r.employees_count}</TableCell>
                    <TableCell className="text-sm font-tabular text-right">{fmt(r.total_gross)}</TableCell>
                    <TableCell className="text-sm font-tabular text-right">{fmt(r.total_net)}</TableCell>
                    <TableCell><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{r.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => handleViewInhand(r)} data-testid={`view-inhand-${r.id}`}><Eye className="w-3 h-3 mr-1" />View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selectedRun && (
        <Card className="border-slate-200" data-testid="inhand-sheet">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div>
                <CardTitle className="text-sm font-semibold">Actual In Hand Salary — {selectedRun.period}</CardTitle>
                <p className="text-[10px] text-slate-400 mt-1">In Hand = Net Payable − Advance Deduction for {selectedRun.period}. Enter a deduction and press Save — it records a recovery against the advance ledger.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1" data-testid="export-inhand-csv"
                  onClick={async () => { try { const r = await api.get(`/api/payroll/advances/export?format=csv&month=${selectedRun.month}&year=${selectedRun.year}`, {responseType: 'blob'}); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = `inhand_${selectedRun.period.replace('/', '_')}.csv`; a.click(); window.URL.revokeObjectURL(u); toast.success('CSV exported'); } catch(e) { toast.error('Export failed'); } }}>
                  <Download className="w-3 h-3" />CSV
                </Button>
                <Button variant="outline" size="sm" className="gap-1" data-testid="export-inhand-pdf"
                  onClick={async () => { try { const r = await api.get(`/api/payroll/advances/export?format=pdf&month=${selectedRun.month}&year=${selectedRun.year}`, {responseType: 'blob'}); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = `inhand_${selectedRun.period.replace('/', '_')}.pdf`; a.click(); window.URL.revokeObjectURL(u); toast.success('PDF exported'); } catch(e) { toast.error('Export failed'); } }}>
                  <Download className="w-3 h-3" />PDF
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedRun(null); setInhand(null); setDeductEdits({}); }} data-testid="close-inhand">Close</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {inhandLoading || !inhand ? <div className="p-6"><Skeleton className="h-40" /></div> : (
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Employee</TableHead><TableHead>Dept</TableHead>
                  <TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net Payable</TableHead>
                  <TableHead className="text-right">Payable Days</TableHead>
                  <TableHead className="text-right">LOP Days</TableHead>
                  <TableHead className="text-right">Advance</TableHead>
                  <TableHead className="text-right w-[170px]">Advance Deduction</TableHead>
                  <TableHead className="text-right">In Hand</TableHead>
                  <TableHead className="text-right">CTC</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {inhand.rows.map(emp => {
                    const editVal = deductEdits[emp.employee_id];
                    const saving = deductSaving[emp.employee_id];
                    const dedNum = Number(editVal) || 0;
                    const inHand = emp.net_payable - dedNum;
                    return (
                      <TableRow key={emp.employee_id} data-testid={`emp-inhand-row-${emp.employee_id}`}>
                        <TableCell><p className="text-sm font-medium">{emp.employee_name}</p><p className="text-xs text-slate-400">{emp.employee_code}</p></TableCell>
                        <TableCell className="text-sm">{emp.department || '—'}</TableCell>
                        <TableCell className="text-sm font-tabular text-right">{fmt(emp.gross)}</TableCell>
                        <TableCell className="text-sm font-tabular text-right text-red-600">{fmt(emp.deductions)}</TableCell>
                        <TableCell className="text-sm font-tabular text-right font-semibold">{fmt(emp.net_payable)}</TableCell>
                        <TableCell className="text-sm font-tabular text-right" data-testid={`payable-days-${emp.employee_id}`}>{emp.payable_days ?? '—'}</TableCell>
                        <TableCell className="text-sm font-tabular text-right text-red-600" data-testid={`lop-days-${emp.employee_id}`}>{emp.lop_days ?? '—'}</TableCell>
                        <TableCell className="text-sm font-tabular text-right font-semibold text-amber-700" data-testid={`advance-balance-${emp.employee_id}`}>{fmt(emp.outstanding)}</TableCell>
                        <TableCell className="text-right">
                          {emp.outstanding > 0 || dedNum > 0 ? (
                            <div className="flex items-center gap-1.5 justify-end">
                              <Input
                                type="number"
                                min="0"
                                max={emp.outstanding + (emp.month_deduction || 0)}
                                placeholder="0"
                                value={editVal ?? 0}
                                onChange={e => setDeductEdits(prev => ({ ...prev, [emp.employee_id]: e.target.value }))}
                                className="h-8 w-24 text-xs text-right font-tabular"
                                disabled={saving}
                                data-testid={`advance-deduction-input-${emp.employee_id}`}
                              />
                              <Button
                                size="sm" variant="outline" className="h-8 text-xs px-2"
                                disabled={saving || Number(editVal) === (emp.month_deduction || 0)}
                                onClick={() => saveDeduction(emp)}
                                data-testid={`advance-deduction-save-${emp.employee_id}`}
                              >{saving ? '…' : 'Save'}</Button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-tabular text-right font-semibold text-emerald-700" data-testid={`in-hand-${emp.employee_id}`}>{fmt(inHand)}</TableCell>
                        <TableCell className="text-sm font-tabular text-right">{fmt(emp.ctc)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm font-semibold">Advance Ledger</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={empFilter} onValueChange={setEmpFilter}>
                <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="advance-employee-filter"><SelectValue placeholder="Employee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {summary?.employees?.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_name} ({e.employee_code})</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="advance-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? <div className="p-6"><Skeleton className="h-40" /></div> : filtered.length === 0 ? (
            <div className="p-12 text-center"><Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-400">No advances recorded yet</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Employee</TableHead><TableHead>Date</TableHead><TableHead>Reason</TableHead>
                <TableHead className="text-right">Advanced</TableHead><TableHead className="text-right">Recovered</TableHead>
                <TableHead className="text-right">Balance</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map(a => (
                  <TableRow key={a.id} data-testid={`advance-row-${a.id}`}>
                    <TableCell><p className="text-sm font-medium">{a.employee_name}</p><p className="text-xs text-slate-400">{a.employee_code}</p></TableCell>
                    <TableCell className="text-sm">{a.date}</TableCell>
                    <TableCell className="text-sm max-w-[180px] truncate" title={a.reason}>{a.reason || '—'}</TableCell>
                    <TableCell className="text-sm text-right font-tabular">{fmt(a.amount)}</TableCell>
                    <TableCell className="text-sm text-right font-tabular text-emerald-700">{fmt(a.recovered)}</TableCell>
                    <TableCell className={`text-sm text-right font-tabular font-semibold ${a.balance > 0 ? 'text-red-600' : 'text-slate-500'}`}>{fmt(a.balance)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge className={a.status === 'closed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : a.status === 'cancelled' ? 'bg-slate-100 text-slate-500 hover:bg-slate-100' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'} data-testid={`advance-status-${a.id}`}>{a.status}</Badge>
                        {a.unrecovered && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]" data-testid={`advance-unrecovered-${a.id}`}>unrecovered</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {a.status === 'active' && a.balance > 0 && (
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => openRecovery(a)} data-testid={`record-recovery-${a.id}`}><IndianRupee className="w-3 h-3" />Recover</Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openHistory(a)} data-testid={`recovery-history-${a.id}`}><Eye className="w-3 h-3" /></Button>
                        {a.status === 'active' && a.recovered === 0 && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => handleCancel(a)} data-testid={`cancel-advance-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader><DialogTitle>Grant Salary Advance</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2" data-testid="grant-advance-dialog">
            <div>
              <Label className="text-xs">Employee</Label>
              <Select value={grantForm.employee_id} onValueChange={v => setGrantForm({ ...grantForm, employee_id: v })}>
                <SelectTrigger data-testid="grant-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount (₹)</Label>
                <Input type="number" min="1" value={grantForm.amount} onChange={e => setGrantForm({ ...grantForm, amount: e.target.value })} data-testid="grant-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={grantForm.date} onChange={e => setGrantForm({ ...grantForm, date: e.target.value })} data-testid="grant-date-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Input value={grantForm.reason} onChange={e => setGrantForm({ ...grantForm, reason: e.target.value })} placeholder="e.g. Medical emergency, Personal" data-testid="grant-reason-input" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={grantForm.notes} onChange={e => setGrantForm({ ...grantForm, notes: e.target.value })} placeholder="Any additional details" data-testid="grant-notes-input" />
            </div>
            <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleGrant} disabled={granting} data-testid="grant-submit-btn">
              {granting ? 'Granting...' : 'Grant Advance'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={recoveryOpen} onOpenChange={setRecoveryOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader><DialogTitle>Record Recovery — {recoveryTarget?.employee_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2" data-testid="recovery-dialog">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-slate-50 rounded p-2"><p className="text-[10px] text-slate-500 uppercase">Advanced</p><p className="text-sm font-semibold">{fmt(recoveryTarget?.amount)}</p></div>
              <div className="bg-emerald-50 rounded p-2"><p className="text-[10px] text-slate-500 uppercase">Recovered</p><p className="text-sm font-semibold text-emerald-700">{fmt(recoveryTarget?.recovered)}</p></div>
              <div className="bg-red-50 rounded p-2"><p className="text-[10px] text-slate-500 uppercase">Balance</p><p className="text-sm font-semibold text-red-600">{fmt(recoveryTarget?.balance)}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Recovery Amount (₹)</Label>
                <Input type="number" min="1" max={recoveryTarget?.balance} value={recoveryForm.amount} onChange={e => setRecoveryForm({ ...recoveryForm, amount: e.target.value })} data-testid="recovery-amount-input" />
                <p className="text-[10px] text-slate-400 mt-1">Editable — deduct any amount this month</p>
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={recoveryForm.date} onChange={e => setRecoveryForm({ ...recoveryForm, date: e.target.value })} data-testid="recovery-date-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Payment Mode</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setRecoveryForm({ ...recoveryForm, mode: 'cash' })} data-testid="recovery-mode-cash"
                  className={`text-xs px-3 py-2 rounded-md border text-left transition-colors ${recoveryForm.mode === 'cash' ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                  Cash / Manual
                  <span className="block text-[10px] font-normal opacity-75 mt-0.5">Paid directly (cash/UPI) — not deducted from salary</span>
                </button>
                <button type="button" onClick={() => setRecoveryForm({ ...recoveryForm, mode: 'salary' })} data-testid="recovery-mode-salary"
                  className={`text-xs px-3 py-2 rounded-md border text-left transition-colors ${recoveryForm.mode === 'salary' ? 'bg-blue-50 border-blue-300 text-blue-700 font-semibold' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                  Salary Deduction
                  <span className="block text-[10px] font-normal opacity-75 mt-0.5">Counts in that month's In Hand sheet</span>
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={recoveryForm.notes} onChange={e => setRecoveryForm({ ...recoveryForm, notes: e.target.value })} placeholder={recoveryForm.mode === 'cash' ? 'e.g. Cash received at office' : 'e.g. September salary deduction'} data-testid="recovery-notes-input" />
            </div>
            <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleRecovery} disabled={savingRecovery} data-testid="recovery-submit-btn">
              {savingRecovery ? 'Recording...' : 'Record Recovery'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader><DialogTitle>Recovery History — {historyTarget?.employee_name}</DialogTitle></DialogHeader>
          <div className="mt-2" data-testid="recovery-history-dialog">
            {history.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No recoveries recorded yet</p>
            ) : (
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Mode</TableHead><TableHead>Notes</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
                <TableBody>
                  {history.map(h => (
                    <TableRow key={h.id}>
                      <TableCell className="text-sm">{h.date}</TableCell>
                      <TableCell className="text-sm text-right font-tabular text-emerald-700">{fmt(h.amount)}</TableCell>
                      <TableCell><Badge className={(h.mode || 'salary') === 'cash' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-blue-100 text-blue-700 hover:bg-blue-100'} data-testid={`recovery-mode-badge-${h.id}`}>{(h.mode || 'salary') === 'cash' ? 'Cash' : 'Salary'}</Badge></TableCell>
                      <TableCell className="text-sm max-w-[140px] truncate" title={h.notes}>{h.notes || '—'}</TableCell>
                      <TableCell className="text-xs text-slate-400">{h.recorded_by}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SalaryPreviewDialog({ open, onOpenChange, preview, loading, fmt }) {
  const [expanded, setExpanded] = useState(null);
  const typeColor = {
    lop: 'bg-red-100 text-red-700',
    half_day: 'bg-amber-100 text-amber-700',
    leave_adjusted: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col" data-testid="salary-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Salary Preview — {preview?.period || ''}</DialogTitle>
        </DialogHeader>
        {preview?.existing_run && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="preview-existing-run-note">
            A payroll run already exists for {preview.period}. This preview is a live recompute from current attendance &amp; leave data — nothing is saved.
          </p>
        )}
        {!loading && preview && (
          <p className="text-xs text-slate-500" data-testid="preview-totals">
            Live recompute — no data saved. Click any row to see the day-by-day breakdown.
            Totals: <span className="font-semibold">Gross {fmt(preview.total_gross)}</span> · <span className="font-semibold">Net {fmt(preview.total_net)}</span> · {preview.employees_count} employees
          </p>
        )}
        <div className="overflow-auto flex-1 -mx-6 px-6">
          {loading || !preview ? <Skeleton className="h-64" /> : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Employee</TableHead><TableHead>Dept</TableHead>
                <TableHead className="text-right">Present</TableHead>
                <TableHead className="text-right">Paid Off</TableHead>
                <TableHead className="text-right">Leaves</TableHead>
                <TableHead className="text-right">Half Days</TableHead>
                <TableHead className="text-right">Absent/LOP</TableHead>
                <TableHead className="text-right">Payable Days</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Deductions</TableHead>
                <TableHead className="text-right">Net Payable</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {preview.rows.map(r => (
                  <React.Fragment key={r.employee_id}>
                    <TableRow className="cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(expanded === r.employee_id ? null : r.employee_id)} data-testid={`preview-row-${r.employee_id}`}>
                      <TableCell><p className="text-sm font-medium">{r.employee_name}</p><p className="text-xs text-slate-400">{r.employee_code}</p></TableCell>
                      <TableCell className="text-sm">{r.department || '—'}</TableCell>
                      <TableCell className="text-sm font-tabular text-right">{r.present_days}</TableCell>
                      <TableCell className="text-sm font-tabular text-right">{r.paid_off_days}</TableCell>
                      <TableCell className="text-sm font-tabular text-right text-emerald-700">{r.leave_days}</TableCell>
                      <TableCell className="text-sm font-tabular text-right text-amber-700">{r.half_day_count}</TableCell>
                      <TableCell className="text-sm font-tabular text-right text-red-600">{r.absent_lop_days}</TableCell>
                      <TableCell className="text-sm font-tabular text-right font-semibold" data-testid={`preview-payable-${r.employee_id}`}>{r.payable_days} / {r.monthly_days}</TableCell>
                      <TableCell className="text-sm font-tabular text-right">{fmt(r.gross_earned)}</TableCell>
                      <TableCell className="text-sm font-tabular text-right text-red-600">{fmt(r.deductions)}</TableCell>
                      <TableCell className="text-sm font-tabular text-right font-semibold">{fmt(r.net_payable)}</TableCell>
                    </TableRow>
                    {expanded === r.employee_id && (
                      <TableRow data-testid={`preview-detail-${r.employee_id}`}>
                        <TableCell colSpan={11} className="bg-slate-50/60">
                          {r.day_remarks?.length ? (
                            <div className="flex flex-wrap gap-1.5 py-1">
                              {r.day_remarks.map((d, i) => (
                                <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${typeColor[d.type] || 'bg-slate-100 text-slate-600'}`} title={d.note}>
                                  {d.date.slice(5)} · {d.note}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-slate-400 py-1">Fully present — no deductions or adjustments.</p>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        {!loading && preview?.skipped_employees?.length > 0 && (
          <p className="text-xs text-amber-600" data-testid="preview-skipped">
            {preview.skipped_employees.length} employee(s) skipped — no salary structure: {preview.skipped_employees.map(s => s.name).join(', ')}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

